'use client';

import { ArrowLeft } from 'lucide-react';

import { OrderSummarySkeleton } from './skeletons/OrderSummarySkeleton';

/**
 * Boot loading state for the checkout app, shown while Firebase initialises and
 * cross-app auth resolves.
 *
 * It is the checkout page with its data regions held, not a separate drawing of
 * one. The previous version was a `max-w-5xl` two-column grid on
 * `bg-background`, under a 16-unit header bar — the real page is `max-w-7xl` on
 * `bg-gray-50`, has no header bar at all, and puts a `max-w-2xl` form column
 * beside a `lg:w-96` sticky rail. Every measurement changed at the swap, and a
 * header appeared and then vanished.
 *
 * RENDERED FOR REAL, because a customer arriving from a menu link is owed them
 * immediately and none of them waits on anything: the page background, both
 * column widths, the "Menu" back control — which also means the customer is
 * never stuck on a page they cannot leave — and the word "Checkout".
 *
 * The order summary reuses `OrderSummarySkeleton`, the SAME component the page
 * itself shows while totals are recalculating. One definition, so the boot state
 * and the in-page state cannot drift apart.
 *
 * Spinner-free, and deliberately unbranded: this is a customer surface.
 */
export function CheckoutLoadingFallback() {
    return (
        <div
            role="status"
            aria-label="Loading checkout"
            aria-busy
            className="min-h-svh bg-gray-50"
        >
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col lg:flex-row lg:gap-8">
                    {/* Left: the form column. */}
                    <div className="flex-1 bg-white lg:bg-transparent">
                        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-12">
                            <button
                                type="button"
                                onClick={() => window.history.back()}
                                className="flex items-center gap-2 text-gray-700 hover:text-gray-900 mb-6 transition-colors"
                            >
                                <ArrowLeft className="h-5 w-5" />
                                <span className="font-medium">Menu</span>
                            </button>

                            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8">
                                Checkout
                            </h1>

                            {/* Contact and payment sections. */}
                            <div className="space-y-8">
                                {[0, 1].map((section) => (
                                    <div key={section} className="space-y-4">
                                        <div className="h-6 w-40 rounded bg-gray-200 motion-safe:animate-pulse" />
                                        <div className="h-11 w-full rounded-lg bg-gray-200 motion-safe:animate-pulse" />
                                        <div className="h-11 w-full rounded-lg bg-gray-200 motion-safe:animate-pulse" />
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="h-11 w-full rounded-lg bg-gray-200 motion-safe:animate-pulse" />
                                            <div className="h-11 w-full rounded-lg bg-gray-200 motion-safe:animate-pulse" />
                                        </div>
                                    </div>
                                ))}
                                <div className="h-12 w-full rounded-lg bg-gray-200 motion-safe:animate-pulse" />
                            </div>
                        </div>
                    </div>

                    {/* Right: the sticky summary rail, at the page's own width. */}
                    <div className="hidden lg:block lg:w-96 xl:w-md">
                        <div className="sticky top-8 space-y-4 pt-12">
                            <OrderSummarySkeleton />
                        </div>
                    </div>
                </div>
            </div>

            <span className="sr-only">Loading checkout…</span>
        </div>
    );
}
